// Platform administration: API keys, credits, usage, projects, old
// hostnames, sessions, image builds, what agents looked for and didn't
// find, config, logs, read-only table inspection and read-only SQL.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import {
  AccessBadge, CopyBox, DailyBarChart, Empty, ErrorMessage, Loading, LogsModal, Meter, Modal, Spinner, StatTile, Tabs,
  formatBytes, formatCredits, relativeTime, useConfirm,
} from '../components/ui';
import type { Project } from '../types';

type TabId =
  | 'overview' | 'keys' | 'credits' | 'usage' | 'projects' | 'hostnames' | 'sessions' | 'capacity' | 'imageBuilds' | 'misses' | 'config'
  | 'logs' | 'tables' | 'query';

interface Overview {
  env: string;
  version: string;
  uptimeSeconds: number;
  schemaVersion: { version: string; applied_at: string } | null;
  counts: { apiKeys: number; projects: number; dockerApps: number };
  runtime: { active: number };
}

interface AdminApiKey {
  id: number;
  alias: string;
  email: string | null;
  disabled: boolean;
  balance: number;
  project_count: number;
  created_at: string;
}

const ADMIN_TABS: { id: TabId; label: string }[] = [
  { id: 'capacity', label: 'Capacity' },
  { id: 'overview', label: 'Overview' },
  { id: 'keys', label: 'API Keys' },
  { id: 'credits', label: 'Credits' },
  { id: 'usage', label: 'Usage' },
  { id: 'projects', label: 'Projects' },
  { id: 'hostnames', label: 'Old hostnames' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'imageBuilds', label: 'Image Builds' },
  { id: 'misses', label: 'Misses' },
  { id: 'config', label: 'Config' },
  { id: 'logs', label: 'Logs' },
  { id: 'tables', label: 'Tables' },
  { id: 'query', label: 'Query' },
];

export default function AdminPage() {
  const navigate = useNavigate();
  // `/admin/*` (App.tsx) — the tab lives in the URL so a link to e.g.
  // /admin/keys is shareable/bookmarkable and survives a refresh, rather
  // than always resetting to the first tab.
  const { '*': rest } = useParams();
  const requested = (rest || '').split('/')[0] as TabId;
  const tab = ADMIN_TABS.some((t) => t.id === requested) ? requested : ADMIN_TABS[0].id;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Admin</h1>
          <p>Docker projects, credits, keys, image builds, config and logs.</p>
        </div>
      </div>

      <Tabs<TabId> active={tab} onChange={(id) => navigate(`/${id}`)} tabs={ADMIN_TABS} />

      {tab === 'overview' ? <OverviewTab /> : null}
      {tab === 'keys' ? <KeysTab /> : null}
      {tab === 'credits' ? <CreditsTab /> : null}
      {tab === 'usage' ? <UsageTab /> : null}
      {tab === 'projects' ? <ProjectsTab /> : null}
      {tab === 'hostnames' ? <HostnamesTab /> : null}
      {tab === 'sessions' ? <SessionsTab /> : null}
      {tab === 'capacity' ? <CapacityTab /> : null}
      {tab === 'imageBuilds' ? <ImageBuildsTab /> : null}
      {tab === 'misses' ? <MissesTab /> : null}
      {tab === 'config' ? <ConfigTab /> : null}
      {tab === 'logs' ? <LogsTab /> : null}
      {tab === 'tables' ? <TablesTab /> : null}
      {tab === 'query' ? <QueryTab /> : null}
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<Overview>('/api/v1/admin').then(setData).catch(setError);
  }, []);

  if (error) return <ErrorMessage error={error} />;
  if (!data) return <Loading />;

  return (
    <>
      <div className="grid grid-3" style={{ marginBottom: '1.25rem' }}>
        <StatTile label="API keys" value={data.counts.apiKeys} />
        <StatTile label="Projects" value={data.counts.projects} />
        <StatTile label="Docker apps" value={data.counts.dockerApps} />
        <StatTile label="Active sessions" value={data.runtime.active} />
        <StatTile label="Uptime" value={`${Math.round(data.uptimeSeconds / 60)}m`} hint={`v${data.version} · ${data.env}`} />
      </div>

      <div className="card">
        <div className="card-title">
          <h3>Database</h3>
        </div>
        <p className="muted">
          Schema version <span className="mono">{data.schemaVersion?.version || 'unknown'}</span>
          {data.schemaVersion ? ` applied ${relativeTime(data.schemaVersion.applied_at)}` : ''}
        </p>
      </div>
    </>
  );
}

function KeysTab() {
  const { confirm, element } = useConfirm();
  const [keys, setKeys] = useState<AdminApiKey[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [grantTarget, setGrantTarget] = useState<AdminApiKey | null>(null);
  const [historyTarget, setHistoryTarget] = useState<AdminApiKey | null>(null);
  const [regenerated, setRegenerated] = useState<{ alias: string; key: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ keys: AdminApiKey[] }>(`/api/v1/admin/keys?q=${encodeURIComponent(search)}`);
      setKeys(data.keys);
    } catch (err) {
      setError(err);
    }
  }, [search]);

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
  }, [load]);

  const update = async (key: AdminApiKey, patch: Record<string, unknown>) => {
    await api.patch(`/api/v1/admin/keys/${key.id}`, patch).catch(setError);
    load();
  };

  const remove = async (key: AdminApiKey) => {
    if (!(await confirm(`Delete ${key.alias} and everything it owns? This cannot be undone.`))) return;
    await api.delete(`/api/v1/admin/keys/${key.id}`).catch(setError);
    load();
  };

  const regenerate = async (key: AdminApiKey) => {
    if (!(await confirm(`Regenerate ${key.alias}'s API key? The old one stops working immediately.`))) return;
    try {
      const result = await api.post<{ key: string }>(`/api/v1/admin/keys/${key.id}/regenerate`);
      setRegenerated({ alias: key.alias, ...result });
    } catch (err) {
      setError(err);
    }
  };

  return (
    <>
      {element}
      <ErrorMessage error={error} />
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by alias or email…"
        style={{ maxWidth: 320, marginBottom: '1rem' }}
      />

      {!keys ? (
        <Loading />
      ) : (
        <div className="card table-wrap">
          <table>
            <thead>
              <tr>
                <th>Alias</th>
                <th>Email</th>
                <th>Credits</th>
                <th>Projects</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id} style={{ opacity: key.disabled ? 0.5 : 1 }}>
                  <td className="mono small">{key.alias}</td>
                  <td className="faint">{key.email || '—'}</td>
                  <td>{formatCredits(key.balance)}</td>
                  <td className="faint">{key.project_count}</td>
                  <td className="faint">{relativeTime(key.created_at)}</td>
                  <td>
                    <div className="row row-tight">
                      <button type="button" className="btn btn-sm" onClick={() => setGrantTarget(key)}>
                        Grant
                      </button>
                      <button type="button" className="btn btn-sm" onClick={() => setHistoryTarget(key)}>
                        History
                      </button>
                      <button type="button" className="btn btn-sm" onClick={() => regenerate(key)}>
                        Regenerate
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => update(key, { disabled: !key.disabled })}
                      >
                        {key.disabled ? 'Enable' : 'Disable'}
                      </button>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => remove(key)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {grantTarget ? (
        <GrantModal
          apiKey={grantTarget}
          onClose={() => setGrantTarget(null)}
          onGranted={() => {
            setGrantTarget(null);
            load();
          }}
        />
      ) : null}

      {historyTarget ? (
        <TransactionHistoryModal apiKey={historyTarget} onClose={() => setHistoryTarget(null)} />
      ) : null}

      {regenerated ? (
        <Modal title={`New API key for ${regenerated.alias}`} onClose={() => setRegenerated(null)}>
          <p className="muted small">Shown once — copy it now. The old key no longer works.</p>
          <div className="field">
            <label>API key</label>
            <CopyBox value={regenerated.key} masked />
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function GrantModal({
  apiKey,
  onClose,
  onGranted,
}: {
  apiKey: AdminApiKey;
  onClose: () => void;
  onGranted: () => void;
}) {
  const [amount, setAmount] = useState('10');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState('admin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/v1/admin/credits/grant', {
        alias: apiKey.alias,
        amount: Number(amount),
        description,
        source: source.trim() || 'admin',
      });
      onGranted();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Modal title={`Grant credits to ${apiKey.alias}`} onClose={onClose}>
      <ErrorMessage error={error} />
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="amount">Amount</label>
          <input id="amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
          <div className="hint">Use a negative amount to deduct.</div>
        </div>
        <div className="field">
          <label htmlFor="grant-source">Source</label>
          <input id="grant-source" type="text" list="grant-source-options" value={source} onChange={(e) => setSource(e.target.value)} />
          <datalist id="grant-source-options">
            <option value="admin" />
            <option value="topup" />
          </datalist>
          <div className="hint">Tracks where these credits came from — shown in this key&rsquo;s transaction history.</div>
        </div>
        <div className="field">
          <label htmlFor="grant-note">Note</label>
          <input id="grant-note" type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? <Spinner /> : 'Grant'}
        </button>
      </form>
    </Modal>
  );
}

function CreditsTab() {
  const [balances, setBalances] = useState<{ id: number; alias: string; email: string | null; balance: number }[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [historyTarget, setHistoryTarget] = useState<{ id: number; alias: string } | null>(null);

  useEffect(() => {
    api
      .get<{ balances: typeof balances }>('/api/v1/admin/credits')
      .then((d) => setBalances(d.balances))
      .catch(setError);
  }, []);

  if (error) return <ErrorMessage error={error} />;
  if (!balances) return <Loading />;

  return (
    <>
      <div className="card table-wrap">
        <table>
          <thead>
            <tr>
              <th>Alias</th>
              <th>Email</th>
              <th style={{ textAlign: 'right' }}>Balance</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {balances.map((row) => (
              <tr key={row.id}>
                <td className="mono small">{row.alias}</td>
                <td className="faint">{row.email || '—'}</td>
                <td style={{ textAlign: 'right' }}>{formatCredits(row.balance)}</td>
                <td>
                  <button type="button" className="btn btn-sm" onClick={() => setHistoryTarget({ id: row.id, alias: row.alias })}>
                    History
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {historyTarget ? (
        <TransactionHistoryModal apiKey={historyTarget} onClose={() => setHistoryTarget(null)} />
      ) : null}
    </>
  );
}

/** Where an API key's credits came from — support-facing view of the same
 *  ledger a key could see about itself via GET /api/v1/credits/transactions. */
function TransactionHistoryModal({ apiKey, onClose }: { apiKey: { id: number; alias: string }; onClose: () => void }) {
  interface Transaction {
    id: number;
    amount: number;
    type: string;
    source: string | null;
    description: string | null;
    created_at: string;
  }
  const [transactions, setTransactions] = useState<Transaction[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api
      .get<{ transactions: Transaction[] }>(`/api/v1/admin/credits/${apiKey.id}/transactions`)
      .then((d) => setTransactions(d.transactions))
      .catch(setError);
  }, [apiKey.id]);

  return (
    <Modal title={`Transaction history — ${apiKey.alias}`} onClose={onClose}>
      <ErrorMessage error={error} />
      {!transactions ? (
        <Loading />
      ) : transactions.length === 0 ? (
        <p className="faint">No transactions yet.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Type</th>
                <th>Source</th>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.id}>
                  <td className="faint" title={t.created_at}>
                    {relativeTime(t.created_at)}
                  </td>
                  <td>
                    <span className="badge">{t.type}</span>
                  </td>
                  <td className="faint small">{t.source || '—'}</td>
                  <td className="truncate" style={{ maxWidth: 260 }}>
                    {t.description}
                  </td>
                  <td style={{ textAlign: 'right', color: t.amount >= 0 ? 'var(--success)' : 'var(--text-muted)' }}>
                    {t.amount >= 0 ? '+' : ''}
                    {formatCredits(t.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function UsageTab() {
  const [data, setData] = useState<{
    topKeys: { alias: string; email: string | null; credits: number; seconds: number }[];
    daily: { day: string; credits: number; seconds: number }[];
    compute: { credits: number; seconds: number };
  } | null>(null);
  const [days, setDays] = useState(7);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setData(null);
    api.get<typeof data>(`/api/v1/admin/usage?days=${days}`).then(setData).catch(setError);
  }, [days]);

  if (error) return <ErrorMessage error={error} />;
  if (!data) return <Loading />;

  const chartData = data.daily.map((point) => ({ day: point.day, value: Number(point.credits || 0) }));

  return (
    <>
      <div className="row" style={{ marginBottom: '1rem' }}>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ maxWidth: 160 }}>
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      <div className="grid grid-3" style={{ marginBottom: '1.25rem' }}>
        <StatTile label="Compute credits" value={formatCredits(Number(data.compute?.credits || 0))} />
        <StatTile label="Compute hours" value={(Number(data.compute?.seconds || 0) / 3600).toFixed(1)} />
        <StatTile label="Top consumer" value={data.topKeys[0]?.alias || '—'} hint={data.topKeys[0] ? formatCredits(data.topKeys[0].credits) : undefined} />
      </div>

      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div className="card-title">
          <h3>Compute credits per day</h3>
        </div>
        <DailyBarChart data={chartData} tone="indigo" />
      </div>

      <div className="card">
        <div className="card-title">
          <h3>Top consumers (container + build compute)</h3>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Alias</th>
                <th style={{ textAlign: 'right' }}>Hours</th>
                <th style={{ textAlign: 'right' }}>Credits</th>
              </tr>
            </thead>
            <tbody>
              {data.topKeys.map((row) => (
                <tr key={row.alias}>
                  <td className="mono small">{row.alias}</td>
                  <td style={{ textAlign: 'right' }}>{(Number(row.seconds || 0) / 3600).toFixed(1)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCredits(Number(row.credits || 0))}</td>
                </tr>
              ))}
              {!data.topKeys.length ? (
                <tr>
                  <td colSpan={3}>
                    <Empty icon="◌" title="No usage yet" />
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

interface Misses {
  days: number;
  kind: 'miss' | 'search';
  total: number;
  paths: { path: string; count: number; firstAt: string; lastAt: string; topUserAgent: string | null; userAgents: number; results: number | null }[];
  recent: { at: string; host: string | null; path: string; results: number | null; userAgent: string | null; accept: string | null; referer: string | null }[];
  limit: { perMinute: number; hits: number; dropped: number };
}

// What agents asked the root host for and didn't find (a GET with no
// document behind it), and what they searched for with /search — the
// server's models/siteMisses.js. A search that matched nothing is a
// document worth writing.
function MissesTab() {
  const [kind, setKind] = useState<'miss' | 'search'>('miss');
  const [days, setDays] = useState(7);
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [data, setData] = useState<Misses | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    api.get<Misses>(`/api/v1/admin/misses?kind=${kind}&days=${days}`).then(setData).catch(setError);
  }, [kind, days]);

  const isSearch = kind === 'search';
  const paths = data ? data.paths.filter((p) => !(isSearch && unansweredOnly) || p.results === 0) : [];
  const recent = data ? data.recent.filter((r) => !(isSearch && unansweredOnly) || r.results === 0) : [];

  return (
    <>
      <div className="row" style={{ marginBottom: '1rem' }}>
        <select value={kind} onChange={(e) => setKind(e.target.value as 'miss' | 'search')} style={{ maxWidth: 160 }}>
          <option value="miss">Not found</option>
          <option value="search">Searches</option>
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ maxWidth: 160 }}>
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        {isSearch ? (
          <label className="row small" style={{ gap: '.4rem' }}>
            <input type="checkbox" checked={unansweredOnly} onChange={(e) => setUnansweredOnly(e.target.checked)} />
            Unanswered only
          </label>
        ) : null}
      </div>

      {error ? <ErrorMessage error={error} /> : null}
      {!data && !error ? <Loading /> : null}
      {data ? (
        <>
          <div className="grid grid-3" style={{ marginBottom: '1.25rem' }}>
            <StatTile label={isSearch ? 'Searches' : 'Not-found requests'} value={String(data.total)} />
            <StatTile label={isSearch ? 'Distinct queries' : 'Distinct paths'} value={String(data.paths.length)} />
            <StatTile
              label="Rate cap reached"
              value={String(data.limit.hits)}
              hint={`${data.limit.dropped} dropped · at most ${data.limit.perMinute} a minute`}
            />
          </div>

          <div className="card" style={{ marginBottom: '1.25rem' }}>
            <div className="card-title">
              <h3>{isSearch ? 'Queries' : 'Paths'}, most asked first</h3>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{isSearch ? 'Query' : 'Path'}</th>
                    <th style={{ textAlign: 'right' }}>Count</th>
                    {isSearch ? <th style={{ textAlign: 'right' }}>Sections matched</th> : null}
                    <th>Last seen</th>
                    <th>Top user agent</th>
                  </tr>
                </thead>
                <tbody>
                  {paths.map((row) => (
                    <tr key={row.path}>
                      <td className="mono small" style={{ wordBreak: 'break-all' }}>{row.path}</td>
                      <td style={{ textAlign: 'right' }}>{row.count}</td>
                      {isSearch ? <td style={{ textAlign: 'right' }}>{row.results === 0 ? <span className="badge badge-warning">none</span> : row.results}</td> : null}
                      <td className="muted small">{relativeTime(row.lastAt)}</td>
                      <td className="muted small" style={{ wordBreak: 'break-word' }}>
                        {row.topUserAgent || '—'}
                        {row.userAgents > 1 ? <span className="faint"> (+{row.userAgents - 1} more)</span> : null}
                      </td>
                    </tr>
                  ))}
                  {!paths.length ? (
                    <tr>
                      <td colSpan={isSearch ? 5 : 4}>
                        <Empty icon="◌" title="Nothing here" />
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-title">
              <h3>Most recent</h3>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Host</th>
                    <th>{isSearch ? 'Query' : 'Path'}</th>
                    <th>User agent</th>
                    <th>Accept</th>
                    <th>Referer</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((row, i) => (
                    <tr key={`${row.at}-${i}`}>
                      <td className="muted small" style={{ whiteSpace: 'nowrap' }}>{relativeTime(row.at)}</td>
                      <td className="mono small">{row.host || '—'}</td>
                      <td className="mono small" style={{ wordBreak: 'break-all' }}>{row.path}</td>
                      <td className="muted small" style={{ wordBreak: 'break-word' }}>{row.userAgent || '—'}</td>
                      <td className="muted small" style={{ wordBreak: 'break-word' }}>{row.accept || '—'}</td>
                      <td className="muted small" style={{ wordBreak: 'break-word' }}>{row.referer || '—'}</td>
                    </tr>
                  ))}
                  {!recent.length ? (
                    <tr>
                      <td colSpan={6}>
                        <Empty icon="◌" title="Nothing here" />
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

function ProjectsTab() {
  const { confirm, element } = useConfirm();
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ projects: Project[] }>(`/api/v1/admin/projects?q=${encodeURIComponent(search)}`);
      setProjects(data.projects);
    } catch (err) {
      setError(err);
    }
  }, [search]);

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
  }, [load]);

  const patch = async (project: Project, body: Record<string, unknown>) => {
    await api.patch(`/api/v1/admin/projects/${project.id}`, body).catch(setError);
    load();
  };

  const remove = async (project: Project) => {
    if (!(await confirm(`Delete ${project.path}? This tears down its running app too.`))) return;
    await api.delete(`/api/v1/admin/projects/${project.id}`).catch(setError);
    load();
  };

  return (
    <>
      {element}
      <ErrorMessage error={error} />
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search projects…"
        style={{ maxWidth: 320, marginBottom: '1rem' }}
      />
      {!projects ? (
        <Loading />
      ) : (
        <div className="card table-wrap">
          <table>
            <thead>
              <tr>
                <th>Path</th>
                <th>Access</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id} style={{ opacity: project.hidden ? 0.5 : 1 }}>
                  <td className="mono small">{project.path}</td>
                  <td>
                    <AccessBadge access={project.access} />
                  </td>
                  <td className="faint">{relativeTime(project.updatedAt)}</td>
                  <td>
                    <div className="row row-tight">
                      <button type="button" className="btn btn-sm" onClick={() => patch(project, { showcase: !project.showcase })}>
                        {project.showcase ? 'Unfeature' : 'Feature'}
                      </button>
                      <button type="button" className="btn btn-sm" onClick={() => patch(project, { hidden: !project.hidden })}>
                        {project.hidden ? 'Unhide' : 'Hide'}
                      </button>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => remove(project)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// Old app hostnames on this datacenter's legacy run domains
// (*.on.{datacenter}.cloudish.ai): apps there are served, or redirected (308,
// path kept) to their url on cloudish.app. The platform's own sites are
// always served. See cloudish's docs/datacenters.md, "Old hostnames".
interface OldHostname {
  host: string;
  domain: string | null;
  kind: 'app' | 'platform' | 'custom';
  project: string | null;
  mode: 'serve' | 'redirect';
  own: boolean;
  preservePath: boolean;
  redirectsTo: string;
  requests: number;
  lastRequestAt: string | null;
  note: string | null;
  updatedBy: string | null;
}

interface LegacyDomain {
  domain: string;
  legacySince: string;
  legacyRedirect: boolean;
}

function HostnamesTab() {
  const { confirm, element } = useConfirm();
  const [data, setData] = useState<{ domains: LegacyDomain[]; hosts: OldHostname[] } | null>(null);
  const [search, setSearch] = useState('');
  const [custom, setCustom] = useState({ host: '', target: '' });
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<{ domains: LegacyDomain[]; hosts: OldHostname[] }>('/api/v1/admin/host-redirects'));
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setMode = async (host: OldHostname, mode: 'serve' | 'redirect') => {
    if (mode === 'redirect' && !(await confirm(`Redirect ${host.host} to ${host.redirectsTo}? Visitors and API calls get a 308 to the new URL.`))) return;
    await api.put(`/api/v1/admin/host-redirects/${encodeURIComponent(host.host)}`, { mode }).catch(setError);
    load();
  };

  const reset = async (host: OldHostname) => {
    const what = host.kind === 'custom' ? `Remove the redirect from ${host.host}?` : `Put ${host.host} back to its domain's default?`;
    if (!(await confirm(what))) return;
    await api.delete(`/api/v1/admin/host-redirects/${encodeURIComponent(host.host)}`).catch(setError);
    load();
  };

  const addCustom = async () => {
    if (!custom.host || !custom.target) return;
    try {
      await api.put(`/api/v1/admin/host-redirects/${encodeURIComponent(custom.host.trim().toLowerCase())}`, { target: custom.target.trim() });
      setCustom({ host: '', target: '' });
      load();
    } catch (err) {
      setError(err);
    }
  };

  const q = search.trim().toLowerCase();
  const hosts = (data?.hosts ?? []).filter((h) => !q || h.host.includes(q) || (h.project ?? '').toLowerCase().includes(q));

  return (
    <>
      {element}
      <ErrorMessage error={error} />
      {data && !data.domains.length ? (
        <Empty title="No legacy domains">This datacenter serves apps on no old domain.</Empty>
      ) : null}
      {data?.domains.map((d) => (
        <p key={d.domain} className="faint small" style={{ marginTop: 0 }}>
          <span className="mono">*.{d.domain}</span>: apps created before {new Date(d.legacySince).toLocaleString()} answer here,{' '}
          {d.legacyRedirect ? 'redirecting by default' : 'served by default'}.
        </p>
      ))}
      <div className="row" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search hostnames or projects…" style={{ maxWidth: 320 }} />
        <input value={custom.host} onChange={(e) => setCustom({ ...custom, host: e.target.value })} placeholder={`faq.${data?.domains[0]?.domain ?? 'on.do.cloudish.ai'}`} style={{ maxWidth: 260 }} />
        <input value={custom.target} onChange={(e) => setCustom({ ...custom, target: e.target.value })} placeholder="https://…" style={{ maxWidth: 280 }} />
        <button type="button" className="btn btn-sm" onClick={addCustom} disabled={!custom.host || !custom.target}>
          Add redirect
        </button>
      </div>
      {!data ? (
        <Loading />
      ) : (
        <div className="card table-wrap">
          <table>
            <thead>
              <tr>
                <th>Hostname</th>
                <th>Project</th>
                <th>Mode</th>
                <th>Redirects to</th>
                <th>Requests</th>
                <th>Last request</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {hosts.map((h) => (
                <tr key={h.host}>
                  <td className="mono small">{h.host}</td>
                  <td className="mono small">{h.kind === 'custom' ? <span className="badge">custom</span> : h.project}</td>
                  <td>
                    <span className={`badge ${h.mode === 'redirect' ? 'badge-warning' : 'badge-ok'}`}>{h.mode}</span>
                    {h.kind === 'platform' ? <span className="faint small"> always</span> : null}
                    {h.own && h.kind === 'app' ? <span className="faint small" title={h.updatedBy ?? ''}> set</span> : null}
                  </td>
                  <td className="mono small faint">{h.redirectsTo}</td>
                  <td>{h.requests}</td>
                  <td className="faint">{relativeTime(h.lastRequestAt)}</td>
                  <td>
                    <div className="row row-tight">
                      {h.kind === 'app' ? (
                        <button type="button" className="btn btn-sm" onClick={() => setMode(h, h.mode === 'redirect' ? 'serve' : 'redirect')}>
                          {h.mode === 'redirect' ? 'Serve' : 'Redirect'}
                        </button>
                      ) : null}
                      {h.own ? (
                        <button type="button" className={`btn btn-sm${h.kind === 'custom' ? ' btn-danger' : ''}`} onClick={() => reset(h)}>
                          {h.kind === 'custom' ? 'Remove' : 'Reset'}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function SessionsTab() {
  const [sessions, setSessions] = useState<
    { id: string; userId: number; path: string | null; workerUrl: string | null; lastSeen: number }[] | null
  >(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(() => {
    api
      .get<{ sessions: typeof sessions }>('/api/v1/admin/sessions')
      .then((d) => setSessions(d.sessions))
      .catch(setError);
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 10_000);
    return () => clearInterval(timer);
  }, [load]);

  const terminate = async (id: string) => {
    await api.delete(`/api/v1/admin/sessions/${id}`).catch(setError);
    load();
  };

  if (error) return <ErrorMessage error={error} />;
  if (!sessions) return <Loading />;
  if (!sessions.length) return <Empty icon="◌" title="No running docker apps" />;

  return (
    <div className="card table-wrap">
      <table>
        <thead>
          <tr>
            <th>Session</th>
            <th>Owner</th>
            <th>Project</th>
            <th>Last used</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {sessions.map((session) => (
            <tr key={session.id}>
              <td className="mono small">{session.id.slice(0, 8)}</td>
              <td className="faint">{session.userId}</td>
              <td className="mono small">{session.path || '—'}</td>
              <td className="faint">{relativeTime(new Date(session.lastSeen).toISOString())}</td>
              <td>
                <button type="button" className="btn btn-sm btn-danger" onClick={() => terminate(session.id)}>
                  Terminate
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface CapacityPool {
  key: 'systemPool' | 'workerPool' | 'buildPool';
  name: string | null;
  size: string | null;
  label: string | null;
  minNodes: number | null;
  maxNodes: number | null;
  autoscalerMaxNodes: number | null;
  currentNodes: number;
  pods: { running: number; allocatable: number };
  cpu: { allocatableCores: number; requestedCores: number };
  memory: { allocatableBytes: number; requestedBytes: number };
}

interface CapacityNode {
  name: string;
  pool: string;
  ready: boolean;
  capacity: { cpuCores: number; memoryBytes: number; pods: number };
  allocatable: { cpuCores: number; memoryBytes: number; pods: number };
  requested: { cpuCores: number; memoryBytes: number };
  usage: { cpuCores: number; memoryBytes: number } | null;
  podsRunning: number;
}

interface CapacityNodeGroup {
  id: string;
  name: string | null;
  pool: string | null;
  minSize: number | null;
  maxSize: number | null;
  target: number | null;
  ready: number | null;
  nodes: string[];
  health: string | null;
  scaleUp: string | null;
  scaleDown: string | null;
  backoff: { code: string; message: string } | null;
}

interface CapacityHealth {
  pct: number;
  level: 'ok' | 'warning' | 'critical';
  thresholds: { warn: number; critical: number };
  pool: string;
  resource: 'cpu' | 'memory' | 'pods';
  nodeLimit: number | null;
  nodeLimitRemaining: number | null;
  pools: {
    key: string; fixed: boolean; addableNodes: number; headroom: number; pct: number; resource: string;
    resources: { resource: string; requested: number; ceiling: number; pct: number }[];
  }[];
}

interface CapacityReport {
  inCluster: boolean;
  datacenter: string | null;
  metricsAvailable: boolean;
  error?: string;
  pools: CapacityPool[];
  nodes: CapacityNode[];
  autoscaler: {
    status: string | null;
    lastProbeTime: string | null;
    scaleUp: string | null;
    scaleDown: string | null;
    nodeGroups: CapacityNodeGroup[];
  } | null;
  health: CapacityHealth | null;
  totals: {
    nodes: {
      current: number; max: number | null;
      maxSource: 'autoscaler' | 'config' | null; configuredMax: number | null;
    };
    pods: { running: number; allocatable: number; utilizationPct: number };
    cpu: {
      allocatableCores: number; requestedCores: number; usedCores: number | null;
      requestedUtilizationPct: number; usedUtilizationPct: number | null;
    };
    memory: {
      allocatableBytes: number; requestedBytes: number; usedBytes: number | null;
      requestedUtilizationPct: number; usedUtilizationPct: number | null;
    };
  } | null;
}

const HEALTH_TONES: Record<CapacityHealth['level'], { tone: string; title: string }> = {
  ok: { tone: 'good', title: 'Plenty of room' },
  warning: { tone: 'warning', title: 'Getting tight' },
  critical: { tone: 'danger', title: 'Running out of nodes' },
};

const RESOURCE_LABELS: Record<string, string> = { cpu: 'CPU', memory: 'memory', pods: 'pod slots' };

const POOL_LABELS: Record<string, string> = {
  systemPool: 'System pool',
  workerPool: 'Worker pool — docker apps',
  buildPool: 'Build pool — image builds',
};

function formatCores(cores: number): string {
  return `${cores.toFixed(cores < 10 ? 1 : 0)} cores`;
}

function utilizationPct(used: number, total: number): number {
  return total > 0 ? (used / total) * 100 : 0;
}

/**
 * Kubernetes capacity: how many nodes each node pool can scale to — the
 * cluster-autoscaler's own live limits where one runs, else what
 * k8s/chart/values-<datacenter>.yaml says (both read server-side by
 * runtime/capacity.js) — vs. how many are running right now, and how much of
 * that capacity — pods, cpu, memory — is already spoken for. "Requested"
 * utilization is what actually gates whether a new pod schedules and needs
 * no extra cluster component; "actual usage" (metrics-server) is shown
 * alongside it when available, never in its place.
 */
function CapacityTab() {
  const [data, setData] = useState<CapacityReport | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(() => {
    api.get<CapacityReport>('/api/v1/admin/capacity').then(setData).catch(setError);
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 15_000);
    return () => clearInterval(timer);
  }, [load]);

  if (error) return <ErrorMessage error={error} />;
  if (!data) return <Loading />;

  if (!data.inCluster) {
    return (
      <Empty icon="⎈" title="Not running in a Kubernetes cluster">
        Capacity reporting needs the control plane's own in-cluster service account — nothing to report in local dev.
      </Empty>
    );
  }

  if (data.error || !data.totals) {
    return <ErrorMessage error={data.error || 'capacity data unavailable'} />;
  }

  const { totals, autoscaler } = data;
  const nodesHint = [
    data.datacenter ? `${data.datacenter} datacenter` : null,
    totals.nodes.maxSource === 'autoscaler'
      ? 'max from cluster-autoscaler'
      : totals.nodes.maxSource === 'config'
        ? `max from values-${data.datacenter}.yaml`
        : autoscaler ? null : 'no autoscaler — fixed nodes',
  ].filter(Boolean).join(' — ');
  const backedOff = autoscaler?.nodeGroups.filter((g) => g.backoff) || [];

  return (
    <>
      {data.health ? <CapacityHealthBar health={data.health} /> : null}

      <div className="grid grid-3" style={{ marginBottom: '1.25rem' }}>
        <StatTile
          label="Nodes"
          value={totals.nodes.max != null ? `${totals.nodes.current} / ${totals.nodes.max}` : totals.nodes.current}
          hint={nodesHint || undefined}
        />
        <StatTile
          label="Pods running"
          value={`${totals.pods.running} / ${totals.pods.allocatable}`}
          hint="scheduling capacity across all nodes"
        />
        <StatTile
          label="Live metrics"
          value={data.metricsAvailable ? 'available' : 'unavailable'}
          hint={data.metricsAvailable ? 'metrics-server' : 'showing scheduled requests only'}
        />
      </div>

      {backedOff.length ? (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="card-title">
            <h3>Autoscaler can't grow {backedOff.length === 1 ? 'a node group' : `${backedOff.length} node groups`}</h3>
            <span className="badge badge-error">Backoff</span>
          </div>
          {backedOff.map((group) => (
            <p className="faint small" key={group.id}>
              <span className="mono">{group.name || group.id}</span>: {group.backoff!.message || group.backoff!.code}
            </p>
          ))}
        </div>
      ) : null}

      <div className="card">
        <div className="card-title">
          <h3>Cluster-wide utilization</h3>
        </div>
        <div className="grid grid-3">
          <Meter
            label="Pod slots"
            value={totals.pods.utilizationPct}
            detail={`${totals.pods.running} / ${totals.pods.allocatable} pods`}
          />
          <Meter
            label="CPU requested"
            value={totals.cpu.requestedUtilizationPct}
            detail={`${formatCores(totals.cpu.requestedCores)} / ${formatCores(totals.cpu.allocatableCores)} allocatable`}
          />
          <Meter
            label="Memory requested"
            value={totals.memory.requestedUtilizationPct}
            detail={`${formatBytes(totals.memory.requestedBytes)} / ${formatBytes(totals.memory.allocatableBytes)} allocatable`}
          />
        </div>
        {data.metricsAvailable && totals.cpu.usedCores != null && totals.memory.usedBytes != null ? (
          <div className="grid grid-2" style={{ marginTop: '1rem' }}>
            <Meter
              label="CPU — actual usage"
              value={totals.cpu.usedUtilizationPct ?? 0}
              detail={`${formatCores(totals.cpu.usedCores)} / ${formatCores(totals.cpu.allocatableCores)} allocatable`}
            />
            <Meter
              label="Memory — actual usage"
              value={totals.memory.usedUtilizationPct ?? 0}
              detail={`${formatBytes(totals.memory.usedBytes)} / ${formatBytes(totals.memory.allocatableBytes)} allocatable`}
            />
          </div>
        ) : null}
      </div>

      <div className="grid grid-2" style={{ marginTop: '1rem' }}>
        {data.pools.map((pool) => (
          <div className="card" key={pool.key}>
            <div className="card-title">
              <h3>{POOL_LABELS[pool.key] || pool.key}</h3>
              <span className="faint small">
                {(pool.autoscalerMaxNodes ?? pool.maxNodes) != null
                  ? `${pool.currentNodes} / ${pool.autoscalerMaxNodes ?? pool.maxNodes} nodes`
                  : `${pool.currentNodes} nodes`}
              </span>
            </div>
            {pool.name ? (
              <p className="faint small" style={{ marginTop: '-.4rem', marginBottom: '.85rem' }}>
                {pool.name}
                {pool.size ? ` · ${pool.size}` : ''}
                {pool.minNodes != null ? ` · min ${pool.minNodes}` : ''}
                {pool.autoscalerMaxNodes != null && pool.maxNodes != null && pool.maxNodes !== pool.autoscalerMaxNodes
                  ? ` · values file says max ${pool.maxNodes}`
                  : ''}
              </p>
            ) : null}
            <div className="grid grid-3">
              <Meter
                label="Pods"
                value={utilizationPct(pool.pods.running, pool.pods.allocatable)}
                detail={`${pool.pods.running} / ${pool.pods.allocatable}`}
              />
              <Meter
                label="CPU"
                value={utilizationPct(pool.cpu.requestedCores, pool.cpu.allocatableCores)}
                detail={formatCores(pool.cpu.requestedCores)}
              />
              <Meter
                label="Memory"
                value={utilizationPct(pool.memory.requestedBytes, pool.memory.allocatableBytes)}
                detail={formatBytes(pool.memory.requestedBytes)}
              />
            </div>
          </div>
        ))}
      </div>

      {autoscaler ? (
        <div className="card table-wrap" style={{ marginTop: '1rem' }}>
          <div className="card-title">
            <h3>Autoscaler node groups</h3>
            <span className="faint small">
              {[autoscaler.status, autoscaler.lastProbeTime ? `probed ${relativeTime(autoscaler.lastProbeTime)}` : null]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          {autoscaler.nodeGroups.length ? (
            <table>
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Pool</th>
                  <th>Ready / target</th>
                  <th>Min – max</th>
                  <th>Scale up</th>
                  <th>Nodes</th>
                </tr>
              </thead>
              <tbody>
                {autoscaler.nodeGroups.map((group) => (
                  <tr key={group.id}>
                    <td className="mono small">{group.name || group.id}</td>
                    <td className="faint small">
                      {group.pool ? POOL_LABELS[group.pool] || group.pool : group.nodes.length ? '—' : 'no nodes yet'}
                    </td>
                    <td className="faint">
                      {group.ready ?? '—'} / {group.target ?? '—'}
                    </td>
                    <td className="faint">
                      {group.minSize ?? '—'} – {group.maxSize ?? '—'}
                    </td>
                    <td>
                      {group.backoff ? (
                        <span className="badge badge-error">Backoff</span>
                      ) : group.scaleUp === 'InProgress' ? (
                        <span className="badge badge-warning">Scaling up</span>
                      ) : (
                        <span className="faint small">{group.scaleUp || '—'}</span>
                      )}
                    </td>
                    <td className="mono small faint">{group.nodes.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty icon="⎈" title="No autoscaling node groups" />
          )}
        </div>
      ) : null}

      <div className="card table-wrap" style={{ marginTop: '1rem' }}>
        <div className="card-title">
          <h3>Nodes</h3>
        </div>
        {data.nodes.length ? (
          <table>
            <thead>
              <tr>
                <th>Node</th>
                <th>Pool</th>
                <th>Status</th>
                <th>Pods</th>
                <th>CPU (requested / allocatable)</th>
                <th>Memory (requested / allocatable)</th>
              </tr>
            </thead>
            <tbody>
              {data.nodes.map((node) => (
                <tr key={node.name} style={{ opacity: node.ready ? 1 : 0.5 }}>
                  <td className="mono small">{node.name}</td>
                  <td className="faint small">{POOL_LABELS[node.pool] || node.pool}</td>
                  <td>
                    {node.ready ? (
                      <span className="badge badge-public">Ready</span>
                    ) : (
                      <span className="badge badge-warning">Not ready</span>
                    )}
                  </td>
                  <td className="faint">
                    {node.podsRunning} / {node.allocatable.pods}
                  </td>
                  <td className="faint">
                    {formatCores(node.requested.cpuCores)} / {formatCores(node.allocatable.cpuCores)}
                  </td>
                  <td className="faint">
                    {formatBytes(node.requested.memoryBytes)} / {formatBytes(node.allocatable.memoryBytes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty icon="⎈" title="No nodes visible" />
        )}
      </div>
    </>
  );
}

/**
 * The one number to watch: what's requested now against the most the
 * cluster can ever reach — including every node the autoscaler could still
 * add, capped by capacityApi.nodeLimit (computed server-side in
 * runtime/capacity.js). Red means the autoscaler can't help any more: the
 * next step is raising the cloud account's limit or adding hardware. The
 * system pool is `fixed` — the platform itself, which never scales — so it
 * keeps its meter but isn't the headline.
 */
function CapacityHealthBar({ health }: { health: CapacityHealth }) {
  const { tone, title } = HEALTH_TONES[health.level];
  const worstPool = health.pools.find((p) => p.key === health.pool);
  const clamped = Math.max(0, Math.min(100, health.pct));
  const roomNote = worstPool
    ? worstPool.addableNodes > 0
      ? `${worstPool.addableNodes} more node${worstPool.addableNodes === 1 ? '' : 's'} can still be added there`
      : 'no more nodes can be added there'
    : '';
  const capNote = health.nodeLimit != null
    ? `${health.nodeLimitRemaining} of ${health.nodeLimit} nodes left under the node limit`
    : 'no node limit configured';
  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div className="card-title">
        <h3>Capacity health — {title}</h3>
        <span className={`meter-value meter-value-${tone}`} style={{ fontSize: '1.25rem' }}>{clamped.toFixed(0)}%</span>
      </div>
      <div className="health-track">
        <div className={`meter-fill meter-fill-${tone}`} style={{ width: `${clamped}%` }} />
        <span className="health-tick" style={{ left: `${health.thresholds.warn}%` }} />
        <span className="health-tick" style={{ left: `${health.thresholds.critical}%` }} />
      </div>
      <p className="faint small" style={{ marginTop: '.6rem' }}>
        Tightest: {POOL_LABELS[health.pool] || health.pool}, {RESOURCE_LABELS[health.resource] || health.resource} —{' '}
        {roomNote}; {capNote}. Measured against everything the cluster can still scale to, so red means the
        autoscaler can't help: raise the account's node limit or add hardware.
      </p>
      <div className="grid grid-3" style={{ marginTop: '.85rem' }}>
        {health.pools.map((pool) => (
          <Meter
            key={pool.key}
            label={POOL_LABELS[pool.key] || pool.key}
            value={pool.pct}
            thresholds={health.thresholds}
            detail={pool.fixed
              ? `${RESOURCE_LABELS[pool.resource] || pool.resource} · fixed size, not in the headline`
              : `${RESOURCE_LABELS[pool.resource] || pool.resource} · ${pool.addableNodes} addable node${pool.addableNodes === 1 ? '' : 's'}`}
          />
        ))}
      </div>
    </div>
  );
}

interface ImageBuilderStatus {
  enabled: boolean;
  inCluster: boolean;
  registryHealthy: boolean;
  buildsInFlight: number;
}

interface ImageBuild {
  id: number;
  owner_alias: string;
  project_name: string | null;
  project_path: string | null;
  repo: string;
  tag: string;
  status: 'pending' | 'running' | 'succeeded' | 'failed';
  error: string | null;
  credits: number | null;
  created_at: string;
  logs?: string;
}

interface RegistryRepo {
  name: string;
  size: number;
  newestTag: string | null;
}

/**
 * On-demand image builds (Kaniko, pushing to the self-hosted Zot registry —
 * see server/src/runtime/builder.js and registry.js). Two independent
 * things live here: cloudish's own build history (who built what, billed how
 * much — Zot has no concept of this) and the registry's own contents (size,
 * delete), sourced live from Zot's search extension rather than cached.
 */
function ImageBuildsTab() {
  const [status, setStatus] = useState<ImageBuilderStatus | null>(null);
  const [builds, setBuilds] = useState<ImageBuild[] | null>(null);
  const [repos, setRepos] = useState<RegistryRepo[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [logsTargetId, setLogsTargetId] = useState<number | null>(null);
  const { confirm, element } = useConfirm();
  // Looked up by id, not stored as its own object — that way it stays in
  // sync with `builds`' own 10s refresh (status flipping to "succeeded" etc.)
  // instead of freezing at whatever it was when the modal was opened.
  const logsTarget = builds?.find((b) => b.id === logsTargetId) || null;
  const fetchBuildLogs = useCallback(
    () => api.get<{ logs: string }>(`/api/v1/admin/image-builds/${logsTargetId}/logs`).then((d) => d.logs),
    [logsTargetId]
  );

  const load = useCallback(() => {
    api.get<ImageBuilderStatus>('/api/v1/admin/image-builder/status').then(setStatus).catch(setError);
    api.get<{ builds: ImageBuild[] }>('/api/v1/admin/image-builds?limit=100').then((d) => setBuilds(d.builds)).catch(setError);
    api.get<{ repos: RegistryRepo[] }>('/api/v1/admin/registry/repos').then((d) => setRepos(d.repos)).catch(setError);
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 10_000);
    return () => clearInterval(timer);
  }, [load]);

  const removeTag = async (repo: string, tag: string) => {
    if (!(await confirm(`Delete ${repo}:${tag} from the registry? This cannot be undone.`))) return;
    await api.delete(`/api/v1/admin/registry/tags?repo=${encodeURIComponent(repo)}&tag=${encodeURIComponent(tag)}`).catch(setError);
    load();
  };

  const cancelBuild = async (build: ImageBuild) => {
    if (!(await confirm(`Cancel build #${build.id} (${build.repo}:${build.tag})?`))) return;
    await api.post(`/api/v1/admin/image-builds/${build.id}/cancel`).catch(setError);
    load();
  };

  const removeBuild = async (build: ImageBuild) => {
    if (!(await confirm(`Delete build #${build.id} (${build.repo}:${build.tag}) from history? This cannot be undone.`))) return;
    await api.delete(`/api/v1/admin/image-builds/${build.id}`).catch(setError);
    load();
  };

  if (error) return <ErrorMessage error={error} />;
  if (!status || !builds || !repos) return <Loading />;

  const statusBadge = (s: ImageBuild['status']) =>
    s === 'succeeded' ? 'badge-ok' : s === 'failed' ? 'badge-error' : 'badge-clamped';

  return (
    <>
      {element}
      <div className="grid grid-3" style={{ marginBottom: '1.25rem' }}>
        <StatTile label="Builds in flight" value={status.buildsInFlight} hint={status.inCluster ? undefined : 'not running in a cluster'} />
        <StatTile label="Registry" value={status.registryHealthy ? 'reachable' : 'unreachable'} hint="self-hosted Zot" />
        <StatTile label="Repositories" value={repos.length} hint={formatBytes(repos.reduce((sum, r) => sum + r.size, 0))} />
      </div>

      <div className="card table-wrap" style={{ marginBottom: '1.25rem' }}>
        <div className="card-title">
          <h3>Builds</h3>
        </div>
        {!builds.length ? (
          <Empty icon="◌" title="No image builds yet" />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Owner</th>
                <th>Project</th>
                <th>Image</th>
                <th>Status</th>
                <th>Credits</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {builds.map((build) => (
                <tr key={build.id}>
                  <td className="faint">{build.owner_alias}</td>
                  <td className="mono small">{build.project_path || '—'}</td>
                  <td className="mono small">{build.repo}:{build.tag}</td>
                  <td><span className={`badge ${statusBadge(build.status)}`}>{build.status}</span></td>
                  <td className="faint">{build.credits != null ? formatCredits(build.credits) : '—'}</td>
                  <td className="faint">{relativeTime(build.created_at)}</td>
                  <td>
                    <div className="row">
                      <button type="button" className="btn btn-sm" onClick={() => setLogsTargetId(build.id)}>
                        Logs
                      </button>
                      {build.status === 'pending' || build.status === 'running' ? (
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => cancelBuild(build)}>
                          Cancel
                        </button>
                      ) : (
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => removeBuild(build)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card table-wrap">
        <div className="card-title">
          <h3>Registry</h3>
        </div>
        {!repos.length ? (
          <Empty icon="◌" title="No images in the registry yet" />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Repository</th>
                <th>Size</th>
                <th>Newest tag</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {repos.map((repo) => (
                <tr key={repo.name}>
                  <td className="mono small">{repo.name}</td>
                  <td className="faint">{formatBytes(repo.size)}</td>
                  <td className="mono small">{repo.newestTag || '—'}</td>
                  <td>
                    {repo.newestTag ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => removeTag(repo.name, repo.newestTag as string)}
                      >
                        Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {logsTarget ? (
        <LogsModal
          title={`Build #${logsTarget.id} — ${logsTarget.repo}:${logsTarget.tag}`}
          status={logsTarget.status}
          error={logsTarget.error}
          downloadName={`build-${logsTarget.id}.log`}
          fetchLogs={fetchBuildLogs}
          onClose={() => setLogsTargetId(null)}
        />
      ) : null}
    </>
  );
}

interface Setting {
  key: string;
  group: string;
  label: string;
  secret: boolean;
  options: string[] | null;
  value: string | null;
  configured: boolean;
  source: 'database' | 'file' | 'unset';
  updatedAt: string | null;
}

interface ConfigPayload {
  config: Record<string, unknown>;
  overrides: { key: string; value: unknown; updated_at: string }[];
  settings: Setting[];
}

function ConfigTab() {
  const [data, setData] = useState<ConfigPayload | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    api.get<ConfigPayload>('/api/v1/admin/config').then(setData).catch(setError);
  }, []);

  const saveSetting = async (key: string) => {
    setBusyKey(key);
    setError(null);
    try {
      const next = await api.put<{ settings: Setting[] }>(`/api/v1/admin/config/settings/${key}`, {
        value: values[key],
      });
      setData((prev) => (prev ? { ...prev, settings: next.settings } : prev));
      setValues((prev) => ({ ...prev, [key]: '' }));
    } catch (err) {
      setError(err);
    } finally {
      setBusyKey(null);
    }
  };

  const clearSetting = async (key: string) => {
    setBusyKey(key);
    setError(null);
    try {
      const next = await api.delete<{ settings: Setting[] }>(`/api/v1/admin/config/settings/${key}`);
      setData((prev) => (prev ? { ...prev, settings: next.settings } : prev));
    } catch (err) {
      setError(err);
    } finally {
      setBusyKey(null);
    }
  };

  if (error && !data) return <ErrorMessage error={error} />;
  if (!data) return <Loading />;

  return (
    <>
      <ErrorMessage error={error} />
      <div className="card">
        <div className="card-title">
          <h3>Deployment configuration</h3>
        </div>
        <p className="muted small">Secrets are never returned by this endpoint.</p>
        <pre className="md" style={{ maxHeight: 320, overflow: 'auto' }}>
          <code>{JSON.stringify(data.config, null, 2)}</code>
        </pre>
      </div>

      {[...new Set(data.settings.map((s) => s.group))].map((group) => (
        <div className="card" key={group}>
          <div className="card-title">
            <h3>{group}</h3>
          </div>
          <p className="muted small">
            Stored encrypted and applied without a restart. Values are never read back — submitting a new one replaces
            it, and clearing falls back to config.yaml or the environment.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Setting</th>
                  <th>Status</th>
                  <th>Value</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.settings
                  .filter((setting) => setting.group === group)
                  .map((setting) => (
                    <tr key={setting.key}>
                      <td>
                        <div>{setting.label}</div>
                        <div className="faint small mono">{setting.key}</div>
                      </td>
                      <td>
                        {setting.configured ? (
                          <span className={`badge ${setting.source === 'database' ? 'badge-accent' : ''}`}>
                            {setting.source === 'database' ? 'overridden' : 'from config'}
                          </span>
                        ) : (
                          <span className="faint small">not set</span>
                        )}
                        {setting.updatedAt ? (
                          <div className="faint small">{relativeTime(setting.updatedAt)}</div>
                        ) : null}
                      </td>
                      <td>
                        {setting.options ? (
                          <select
                            value={values[setting.key] || setting.value || ''}
                            onChange={(e) => setValues((prev) => ({ ...prev, [setting.key]: e.target.value }))}
                          >
                            {!setting.value ? <option value="">not set</option> : null}
                            {setting.options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={setting.secret ? 'password' : 'text'}
                            value={values[setting.key] ?? setting.value ?? ''}
                            placeholder={setting.secret && setting.configured ? '••••••••' : 'not set'}
                            autoComplete="off"
                            onChange={(e) => setValues((prev) => ({ ...prev, [setting.key]: e.target.value }))}
                          />
                        )}
                      </td>
                      <td>
                        <div className="row row-tight">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={
                              busyKey === setting.key ||
                              !values[setting.key]?.trim() ||
                              values[setting.key] === setting.value
                            }
                            onClick={() => saveSetting(setting.key)}
                          >
                            {busyKey === setting.key ? <Spinner /> : 'Save'}
                          </button>
                          {setting.source === 'database' ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              disabled={busyKey === setting.key}
                              onClick={() => clearSetting(setting.key)}
                            >
                              Clear
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {data.overrides.length ? (
        <div className="card">
          <div className="card-title">
            <h3>Runtime overrides</h3>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {data.overrides.map((override) => (
                  <tr key={override.key}>
                    <td className="mono small">{override.key}</td>
                    <td className="faint">{relativeTime(override.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}

function LogsTab() {
  const [logs, setLogs] = useState<{ time: string; level: string; message: string }[] | null>(null);
  const [level, setLevel] = useState('');
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(() => {
    api
      .get<{ logs: typeof logs }>(`/api/v1/admin/logs?limit=400${level ? `&level=${level}` : ''}`)
      .then((d) => setLogs(d.logs))
      .catch(setError);
  }, [level]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load]);

  if (error) return <ErrorMessage error={error} />;

  return (
    <>
      <div className="row" style={{ marginBottom: '1rem' }}>
        <select value={level} onChange={(e) => setLevel(e.target.value)} style={{ maxWidth: 160 }}>
          <option value="">All levels</option>
          {['trace', 'debug', 'info', 'warn', 'error'].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="card">
        <pre className="md" style={{ maxHeight: '60vh', overflow: 'auto' }}>
          <code>
            {(logs || [])
              .map((entry) => `${entry.time} ${entry.level.toUpperCase().padEnd(5)} ${entry.message}`)
              .join('\n')}
          </code>
        </pre>
      </div>
    </>
  );
}

const TABLE_PAGE_SIZE = 50;

function TablesTab() {
  const [tables, setTables] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [count, setCount] = useState(0);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<{ tables: string[] }>('/api/v1/admin/tables').then((d) => setTables(d.tables)).catch(setError);
  }, []);

  useEffect(() => {
    if (!selected) return;
    api
      .get<{ rows: Record<string, unknown>[]; count: number }>(
        `/api/v1/admin/tables/${selected}?limit=${TABLE_PAGE_SIZE}&offset=${page * TABLE_PAGE_SIZE}`,
      )
      .then((d) => {
        setRows(d.rows);
        setCount(d.count);
      })
      .catch(setError);
  }, [selected, page]);

  const columns = rows.length ? Object.keys(rows[0]) : [];
  const pageCount = Math.max(1, Math.ceil(count / TABLE_PAGE_SIZE));
  const first = page * TABLE_PAGE_SIZE;

  return (
    <>
      <ErrorMessage error={error} />
      <div className="row" style={{ marginBottom: '1rem' }}>
        <select
          value={selected}
          onChange={(e) => {
            setSelected(e.target.value);
            setPage(0);
          }}
          style={{ maxWidth: 280 }}
        >
          <option value="">Select a table…</option>
          {tables.map((table) => (
            <option key={table} value={table}>
              {table}
            </option>
          ))}
        </select>
        {selected ? <span className="faint small">{count} rows</span> : null}
        {selected && count > TABLE_PAGE_SIZE ? (
          <div className="row" style={{ marginLeft: 'auto' }}>
            <span className="faint small">
              {first + 1}–{Math.min(first + TABLE_PAGE_SIZE, count)} · page {page + 1} of {pageCount}
            </span>
            <button type="button" className="btn btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
              ← Prev
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage(page + 1)}
            >
              Next →
            </button>
          </div>
        ) : null}
      </div>

      {selected && rows.length ? (
        <div className="card table-wrap">
          <table>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  {columns.map((column) => (
                    <td key={column} className="truncate small" style={{ maxWidth: 200 }}>
                      {typeof row[column] === 'object' ? JSON.stringify(row[column]) : String(row[column] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : selected ? (
        <Empty icon="▤" title="Table is empty" />
      ) : null}
    </>
  );
}

interface QueryResult {
  columns: string[];
  rows: unknown[][];
  truncated: boolean;
  elapsedMs: number;
}

/** Ad-hoc SQL against the control plane's Postgres. The server runs it in a
 *  READ ONLY transaction that always rolls back, so nothing typed here can
 *  change data — see the control plane's db/readOnlyQuery.js. */
function QueryTab() {
  const [sql, setSql] = useState('SELECT * FROM projects ORDER BY created_at DESC LIMIT 20');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const run = async () => {
    if (!sql.trim() || running) return;
    setRunning(true);
    setError(null);
    try {
      setResult(await api.post<QueryResult>('/api/v1/admin/query', { sql }));
    } catch (err) {
      setResult(null);
      setError(err);
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <textarea
          className="mono"
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              run();
            }
          }}
          spellCheck={false}
          rows={6}
          style={{ width: '100%' }}
        />
        <div className="row" style={{ marginTop: '0.75rem' }}>
          <button type="button" className="btn btn-primary" disabled={running || !sql.trim()} onClick={run}>
            {running ? <Spinner /> : null} Run
          </button>
          <span className="faint small">
            Read-only · ⌘/Ctrl+Enter to run · up to 1000 rows · API keys are masked
          </span>
        </div>
      </div>

      <ErrorMessage error={error} />

      {result ? (
        <>
          <p className="faint small">
            {result.rows.length} row{result.rows.length === 1 ? '' : 's'}
            {result.truncated ? ' (truncated — add a LIMIT or narrow the query)' : ''} · {result.elapsedMs} ms
          </p>
          {result.rows.length ? (
            <div className="card table-wrap">
              <table>
                <thead>
                  <tr>
                    {result.columns.map((column, index) => (
                      <th key={index}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row, index) => (
                    <tr key={index}>
                      {row.map((value, column) => (
                        <td key={column} className="truncate small" style={{ maxWidth: 200 }}>
                          {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty icon="▤" title="No rows" />
          )}
        </>
      ) : null}
    </>
  );
}
